import "@/hooks/useEditorPrefs"; // Initialize theme on load
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { NewProjectDialogProvider } from "@/hooks/useNewProjectDialog";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { usePageTracking } from "@/hooks/useAnalytics";
import { lazy, Suspense } from "react";
import { PWAManager } from "@/components/PWAManager";

// Lazy-load routes to reduce initial bundle. The IDE (Index) pulls in
// CodeMirror, Sandpack and xterm, so marketing visitors must not download it.
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Landing = lazy(() => import("./pages/Landing"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Settings = lazy(() => import("./pages/Settings"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Admin = lazy(() => import("./pages/Admin"));
const Compare = lazy(() => import("./pages/Compare"));

// Landing sub-pages
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Docs = lazy(() => import("./pages/Docs"));
const GettingStarted = lazy(() => import("./pages/GettingStarted"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Security = lazy(() => import("./pages/Security"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const CookieSettings = lazy(() => import("./pages/CookieSettings"));
const PitchDeck = lazy(() => import("./pages/PitchDeck"));
const BusinessPlan = lazy(() => import("./pages/BusinessPlan"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const VsCompetitor = lazy(() => import("./pages/VsCompetitor"));
const Alternatives = lazy(() => import("./pages/Alternatives"));
const Templates = lazy(() => import("./pages/Templates"));
const TemplateDetail = lazy(() => import("./pages/TemplateDetail"));

const queryClient = new QueryClient();

// Must be rendered inside <BrowserRouter> so useLocation works
function SessionTimeoutWrapper({ children }: { children: React.ReactNode }) {
  useSessionTimeout(30 * 60 * 1000); // 30 minutes
  return <>{children}</>;
}

/** Tracks page views on every route change */
function PageTracker() {
  usePageTracking();
  return null;
}

const RouteFallback = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-background">
    <div className="h-8 w-8 bg-muted rounded animate-pulse" />
  </div>
);

/** Root route: landing page for guests, app for authenticated users */
// index.html's inline guard strips the prerendered landing page for signed-in
// visitors, so if it survived to boot we can keep showing it while auth loads
// instead of flashing a spinner (and it matches the server HTML for hydration).
const bootedFromPrerenderedHome =
  typeof document !== "undefined" && document.getElementById("root")?.dataset.prerendered === "/";

function RootRoute() {
  const { initialized, user } = useAuth();
  // Prerender (build-time SSR) always emits the public landing page for "/"
  if (import.meta.env.SSR) return <Landing />;
  if (!initialized) return bootedFromPrerenderedHome ? <Landing /> : <RouteFallback />;
  if (user) return <Index />;
  return <Landing />;
}

/**
 * `Router` is injectable so the build-time prerenderer (src/entry-server.tsx)
 * can render the same tree inside a StaticRouter.
 */
const App = ({ Router = BrowserRouter }: { Router?: React.ComponentType<{ children: React.ReactNode }> }) => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <NewProjectDialogProvider>
      <TooltipProvider>
        <Sonner />
        <PWAManager />
        <Router>
          {/* SessionTimeoutWrapper is inside BrowserRouter so useLocation works */}
          <SessionTimeoutWrapper>
            <PageTracker />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<RootRoute />} />
                {/* Lets signed-in users view the marketing page; canonical points at "/" */}
                <Route path="/landing" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/vs/:slug" element={<VsCompetitor />} />
                <Route path="/alternatives/:slug" element={<Alternatives />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/templates/:slug" element={<TemplateDetail />} />
                {/* Landing pages */}
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/docs/:section" element={<Docs />} />
                <Route path="/getting-started" element={<GettingStarted />} />
                <Route path="/changelog" element={<Changelog />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/security" element={<Security />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/cookies" element={<CookieSettings />} />
                <Route path="/pitchdeck" element={<PitchDeck />} />
                <Route path="/business-plan" element={<BusinessPlan />} />
                <Route path="/api" element={<Navigate to="/api-docs" replace />} />
                <Route path="/api-docs" element={<ApiDocs />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </SessionTimeoutWrapper>
        </Router>
      </TooltipProvider>
      </NewProjectDialogProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
